import React, { useState } from 'react';

import { Radio } from '../Radio';
import type { IRadioGroupProps } from './RadioGroup.types';

export const RadioGroup: React.FC<IRadioGroupProps> = ({
  name,
  options,
  value,
  defaultValue,
  onChange,
  label,
  description,
  size = 'md',
  direction = 'vertical',
  error = false,
  errorText,
  disabled = false,
  required = false,
  containerClassName = '',
  optionsClassName = '',
}: IRadioGroupProps) => {

  // Internal state for uncontrolled component
  const [internalValue, setInternalValue] = useState(defaultValue || '');
  
  // Use controlled value if provided, otherwise use internal state
  const currentValue = value !== undefined ? value : internalValue;

  const handleChange = (optionValue: string) => {
    if (value === undefined) {
      setInternalValue(optionValue);
    }
    onChange?.(optionValue);
  };

  const directionClass = direction === 'horizontal' ? 'flex-row flex-wrap' : 'flex-col';
  const gapClass = direction === 'horizontal' ? 'gap-bg-v2-lg' : 'gap-bg-v2-md';

  return (
    <div className={`flex flex-col gap-bg-v2-sm ${containerClassName}`}>
      {/* Label */}
      {label && (
        <div className="flex flex-col gap-bg-v2-xxs">
          <label className={`text-bg-v2-sm font-bg-v2-medium ${error ? 'text-text-error-primary' : 'text-text-secondary'}`}>
            {label}
            {required && <span className="text-text-error-primary ml-1">*</span>}
          </label>
          {description && (
            <span className="text-bg-v2-sm text-text-tertiary">
              {description}
            </span>
          )}
        </div>
      )}

      {/* Radio Options */}
      <div className={`flex ${directionClass} ${gapClass} ${optionsClassName}`}>
        {options.map((option) => (
          <Radio
            key={option.value}
            id={`${name}-${option.value}`}
            name={name}
            value={option.value}
            label={option.label}
            labelAddon={option.labelAddon}
            description={option.description}
            size={size}
            error={error}
            disabled={disabled || option.disabled}
            checked={currentValue === option.value}
            onChange={() => handleChange(option.value)}
          />
        ))}
      </div>

      {/* Error Text */}
      {errorText && (
        <span className="text-bg-v2-sm text-text-error-primary">
          {errorText}
        </span>
      )}
    </div>
  );
};

